/** @jsxImportSource @emotion/react */
import React, { useMemo } from 'react'
import { matchPath } from 'react-router'
import { Link, useLocation } from 'react-router-dom'
import { routes } from 'routes/cmsRoutes'
import { ReactComponent as GraduationCapIcon } from 'assets/icons/graduation-cap.svg'
import { ReactComponent as GridPlusIcon } from 'assets/icons/grid-plus.svg'
import { usePlatformsAndSubjects } from 'hooks/usePlatformsAndSubjects'
import { findAllWithSubjectsTabs_findAllWithSubjectsTabs_subjects as PlatformSubject } from '__generatedTypes__/findAllWithSubjectsTabs'
import { CourseName } from 'theme/utils'
import { ClassroomTabEnum, PracticeTestTypeEnum } from '__generatedTypes__/globalTypes'
import { useAuthContext } from 'utils/auth'
import { PlatformSlug } from 'constants/consts'
import { isCaseScenarioPlatform } from 'pagesNew/QuestionBanks/utils'
import {
  isCaseBasedScenariosAvailable,
  isCarsQuestionBanksAvailable,
  isScenariosQBanksAvailable,
} from 'pagesNew/QuestionBanks/platformConfig'
import { AI_TRAINER, MARKETING_GROUP, SUPER_ADMIN_GROUP } from 'utils/services/auth'
import { COMMON_PTS_CONFIG } from 'pagesNew/CommonPracticeTests/consts'
import { ReactComponent as CalendarIcon } from 'assets/icons/calendar.svg'
import { isStudyScheduleAvailable } from 'pagesNew/StudySchedule/platformConfig'
import { isCrashCoursesAvailable } from 'pagesNew/CrashCourses/platformConfig'
import { isBlogPagesAvailable } from 'pagesNew/BlogPages/platformConfig'
import { isCommonElementsAvailable } from 'pagesNew/CommonElements/platformConfig'
import { isTeamAvailable } from 'pagesNew/TeamMembers/platformConfig'
import { isQuestionsAvailable } from 'pagesNew/Questions/platformConfig'
import { isCarsQuestionsAvailable } from 'pagesNew/QuestionPassages/platformConfig'
import { isPluginAvailable } from 'pagesNew/CARSPlugin/platformConfig'
import { isAamcSolutionVideosAvailable, isVideoContentAvailable } from 'pagesNew/Video/platformConfig'
import { isInfluencerHubAvailable } from 'pagesNew/InfluencerHub/platformConfig'
import { isCanadianDatTipsAvailable } from 'pagesNew/CanadianDatTips/platformConfig'
import { isChapterNotesAvailable } from 'pagesNew/ChapterNotes/platformConfig'
import { isMarkedQuestionsAvailable } from 'pagesNew/MarkedQuestions/platformConfig'
import { isCreateTestAvailable } from 'pagesNew/CreateTest/platformConfig'
import { isFullMiniTestsAvailable } from 'pagesNew/FullLengthTests/platformConfig'
import { isPastTestResultsAvailable } from 'pagesNew/PastTestResults/platformConfig'
import { isPracticeResultsAvailable } from 'pagesNew/PracticeResults/platformConfig'
import { isPerformanceAvailable } from 'pagesNew/Performance/platformConfig'
import { isAchievementsAvailable } from 'pagesNew/Achievements/platformConfig'
import { isDailyWarmupAvailable } from 'pagesNew/DailyWarmup/platformConfig'
import { isDailyGoalAvailable } from 'pagesNew/DailyGoal/platformConfig'
import { isGameChallengesAvailable } from 'pagesNew/GameChallenges/platformConfig'
import { isContentCompletionAvailable } from 'pagesNew/ContentCompletion/platformConfig'
import { isStatisticsAvailable } from 'pagesNew/Statistics/platformConfig'
import { isDiscountCodesAvailable } from 'pagesNew/PromoCodes/platformConfig'
import { isInstitutionalCodesAvailable } from 'pagesNew/InstitutionalCodes/platformConfig'
import { isProductsAvailable } from 'pagesNew/Products/platformConfig'
import { isRealStudentReviewsAvailable, isUpgradePageAvailable } from 'pagesNew/UpgradePage/platformConfig'
import { isEmailsAvailable } from 'pagesNew/Emails/platformConfig'
import { isFaqAvailable } from 'pagesNew/FAQ/platformConfig'
import { isAiTrainingAvailable } from 'pagesNew/TrainingData/platformConfig'
import { ReactComponent as UniversalIcon } from './icons/universal.svg'
import { ReactComponent as ArrowRightIcon } from './icons/arrow-right.svg'
import { ReactComponent as CrashCoursesIcon } from './icons/crash-courses.svg'
import { ReactComponent as TeamIcon } from './icons/team.svg'
import { ReactComponent as BlogPagesIcon } from './icons/blog-pages.svg'
import { ReactComponent as TrainingDataIcon } from './icons/training-data.svg'
import { ReactComponent as QuestionsIcon } from './icons/questions.svg'
import { ReactComponent as EmailsIcon } from './icons/emails.svg'
import { ReactComponent as PATGeneratorsIcon } from './icons/pat-generators.svg'
import { ReactComponent as FullLengthTestsIcon } from './icons/full-length-tests.svg'
import { ReactComponent as CarsPracticeTestsIcon } from './icons/cars-practice-tests.svg'
import { ReactComponent as StrategiesIcon } from './icons/strategies.svg'
import { ReactComponent as AccountSharingIcon } from './icons/account-sharing.svg'
import { ReactComponent as VideosIcon } from './icons/videos.svg'
import { ReactComponent as CheatSheetsIcon } from './icons/cheat-sheets.svg'
import { ReactComponent as StudyNotesIcon } from './icons/study-notes.svg'
import { ReactComponent as FlashCardsIcon } from './icons/flash-cards.svg'
import { ReactComponent as CommonElementsIcon } from './icons/common-elements.svg'
import { ReactComponent as CanadianDatTipsIcon } from './icons/canadian-dat-tips.svg'
import { ReactComponent as MarkedQuestionsIcon } from './icons/marked-questions.svg'
import { ReactComponent as PastTestsResultsIcon } from './icons/past-test-results.svg'
import { ReactComponent as PromoCodesIcon } from './icons/promo-codes.svg'
import { ReactComponent as BookIcon } from './icons/book.svg'
import { ReactComponent as PerformanceIcon } from './icons/performance.svg'
import { ReactComponent as AchievementsIcon } from './icons/achievements.svg'
import { ReactComponent as CaseBasedScenarioIcon } from './icons/case-based-scenario.svg'
import { ReactComponent as CommunityTestletsIcon } from './icons/community-testlets.svg'
import { ReactComponent as DailyWarmupIcon } from './icons/daily-warmup.svg'
import { ReactComponent as GamepadIcon } from './icons/gamepad.svg'
import { ReactComponent as CreateTestIcon } from './icons/create-test.svg'
import { ReactComponent as FacultyIcon } from './icons/faculty.svg'
import { isTopicTabsAvailable } from './platformConfig'
import * as classes from './styles'

type MenuAccessContext = {
  subjects: PlatformSubject[]
  isSuperAdmin: boolean
  groups: string[]
  platformSlug?: PlatformSlug
}

type MenuConfigItem = {
  icon: React.ReactElement
  title: string | JSX.Element
  key: string
  classroomTabs?: ClassroomTabEnum[]
  subjectTabNames?: CourseName[]
  disabled?: boolean
  isUniversal?: boolean
  isAvailable?: (platformSlug?: PlatformSlug) => boolean
  roles?: string[]
  canAccess?: (ctx: MenuAccessContext) => boolean
}

type MenuConfig = Array<{
  title: string
  isAvailable?: (platformSlug?: PlatformSlug) => boolean
  items: MenuConfigItem[]
}>

const TOPIC_TABS_TITLE = 'Topic Tabs'
const AI_TITLE = 'AI Training Data Management'

const isCommonPtAvailable = (type: PracticeTestTypeEnum, slug?: PlatformSlug) =>
  !!slug && !!COMMON_PTS_CONFIG[type]?.subjectIds[slug]

const hasClassroomTabs = (classroomTabs: ClassroomTabEnum[] | undefined, subjects: PlatformSubject[]) =>
  !classroomTabs ||
  !!subjects.find(({ classroomTab }) => classroomTab.find(({ tabEnum }) => classroomTabs.includes(tabEnum)))

const hasSubjectTabNames = (subjectTabNames: CourseName[] | undefined, subjects: PlatformSubject[]) =>
  !subjectTabNames || subjects.some((subject) => subjectTabNames.includes(subject.tabName as CourseName))

const isItemVisible = (item: MenuConfigItem, ctx: MenuAccessContext) => {
  if (item.isAvailable && !item.isAvailable(ctx.platformSlug)) {
    return false
  }

  if (item.canAccess && !item.canAccess(ctx)) {
    return false
  }

  if (item.roles && !item.roles.some((role) => ctx.groups.includes(role))) {
    return false
  }

  if (!hasClassroomTabs(item.classroomTabs, ctx.subjects)) {
    return false
  }

  if (!hasSubjectTabNames(item.subjectTabNames, ctx.subjects)) {
    return false
  }

  return true
}

export const AI_MENU_TRAINING_ITEM: MenuConfigItem = {
  icon: <TrainingDataIcon />,
  title: 'Training Data',
  key: routes.TRAINING_DATA,
}

export const AI_MENU_MODEL_TRAIN_ITEM: MenuConfigItem = {
  icon: <TrainingDataIcon />,
  title: 'Train A.I. model',
  key: routes.TRAIN_AI_MODEL,
  roles: [SUPER_ADMIN_GROUP],
}

const AI_MENU: MenuConfig = [
  {
    title: AI_TITLE,
    isAvailable: isAiTrainingAvailable,
    items: [
      AI_MENU_TRAINING_ITEM,
      AI_MENU_MODEL_TRAIN_ITEM,
      {
        icon: <VideosIcon />,
        title: (
          <>
            Video/Text Response
            <br />
            tests
          </>
        ),
        key: routes.RESPONSE_TESTS,
      },
      {
        icon: <FullLengthTestsIcon />,
        title: 'Full-Length Test',
        key: routes.RESPONSE_FULL_LENGTH_TEST,
      },
    ],
  },
]

const BLOG_PAGES_MENU_ITEM: MenuConfigItem = {
  icon: <BlogPagesIcon />,
  title: 'Blog Pages',
  key: routes.BLOG_PAGES,
  isAvailable: isBlogPagesAvailable,
}

const COMMON_ELEMENTS_MENU_ITEM: MenuConfigItem = {
  icon: <CommonElementsIcon />,
  title: 'Common Elements',
  key: routes.COMMON_ELEMENTS,
  isAvailable: isCommonElementsAvailable,
}

export const USERS_MENU_ITEM: MenuConfigItem = {
  icon: <TeamIcon />,
  title: 'Users',
  key: routes.USER_LIST,
  isUniversal: true,
}

export const DISCOUNT_CODES_MENU_ITEM: MenuConfigItem = {
  icon: <PromoCodesIcon />,
  title: 'Discount Codes',
  key: routes.PROMO_CODES_LIST,
  isAvailable: isDiscountCodesAvailable,
}

const MENU: MenuConfig = [
  {
    title: 'General Pages',
    items: [
      {
        icon: <CrashCoursesIcon />,
        title: 'Crash Courses',
        key: routes.CRASH_COURSE_LIST,
        isAvailable: isCrashCoursesAvailable,
      },
      {
        icon: <TeamIcon />,
        title: 'Team',
        key: routes.TEAM_MEMBERS,
        isAvailable: isTeamAvailable,
      },
      BLOG_PAGES_MENU_ITEM,
      COMMON_ELEMENTS_MENU_ITEM,
    ],
  },
  {
    title: TOPIC_TABS_TITLE,
    isAvailable: isTopicTabsAvailable,
    items: [
      {
        icon: <BlogPagesIcon />,
        title: 'Practice Tests',
        key: routes.PRACTICE_TEST_LIST,
        classroomTabs: [ClassroomTabEnum.PracticeTests, ClassroomTabEnum.ReadingPracticeTests],
      },
      {
        icon: <VideosIcon />,
        title: 'Videos',
        key: routes.VIDEO_DASHBOARD,
        classroomTabs: [ClassroomTabEnum.VideoContent],
      },
      {
        icon: <CheatSheetsIcon />,
        title: 'Cheat Sheets',
        key: routes.CHEAT_SHEET,
        classroomTabs: [ClassroomTabEnum.CheatSheets],
      },
      {
        icon: <StudyNotesIcon />,
        title: 'Study Notes',
        key: routes.STUDY_NOTE,
        classroomTabs: [ClassroomTabEnum.StudyNotes],
      },
      {
        icon: <FlashCardsIcon />,
        title: 'Flash Cards',
        key: routes.FLASH_CARD,
        classroomTabs: [ClassroomTabEnum.FlashCards],
      },
      {
        icon: <QuestionsIcon />,
        title: 'Question Banks',
        key: routes.QUESTION_BANK_LIST,
        classroomTabs: [
          ClassroomTabEnum.QuestionBanks,
          ClassroomTabEnum.ReadingQuestionBanks,
          ClassroomTabEnum.ReactionQbank,
        ],
      },
      {
        icon: <CommunityTestletsIcon />,
        title: 'Community Testlets',
        key: routes.COMMUNITY_TESTLETS_LIST,
        classroomTabs: [ClassroomTabEnum.CommunityTestlets],
      },
      {
        icon: <QuestionsIcon />,
        title: 'Standalone QBanks',
        key: routes.OSCE_STANDALON_QB_LIST,
        classroomTabs: [ClassroomTabEnum.StandaloneOSCEQBanks],
      },
      {
        icon: <QuestionsIcon />,
        title: 'Multiselect QBanks',
        key: routes.OSCE_MULTISELECT_QB_LIST,
        classroomTabs: [ClassroomTabEnum.MultiselectOSCEQBanks],
      },
      {
        icon: <QuestionsIcon />,
        title: 'Rx. Writing QBanks',
        key: routes.OSCE_RX_WRITING_QB_LIST,
        classroomTabs: [ClassroomTabEnum.RxWritingOSCEQBanks],
      },
      {
        icon: <StrategiesIcon />,
        title: 'Strategies',
        key: routes.STRATEGIES,
        classroomTabs: [ClassroomTabEnum.ReadingStrategies, ClassroomTabEnum.PATStrategies],
      },
      {
        icon: <BookIcon />,
        title: (
          <>
            Recommended
            <br />
            Articles
          </>
        ),
        key: routes.RECOMMENDED_ARTICLES,
        classroomTabs: [ClassroomTabEnum.RecommendedArticles],
      },
    ],
  },
  ...AI_MENU,
  {
    title: 'LMS',
    items: [
      {
        icon: <QuestionsIcon />,
        title: 'Questions',
        key: routes.QUESTION_LIST,
        isAvailable: isQuestionsAvailable,
        isUniversal: true,
      },
      {
        icon: <QuestionsIcon />,
        title: 'CARS Questions',
        key: routes.CARS_QUESTION_LIST,
        isAvailable: isCarsQuestionsAvailable,
      },
      {
        icon: <CommonElementsIcon />,
        title: 'Plugin',
        key: routes.CARS_PLUGIN,
        isAvailable: isPluginAvailable,
      },
      {
        icon: <VideosIcon />,
        title: 'AAMC Solution Videos',
        key: routes.VIDEO_DASHBOARD,
        isAvailable: isAamcSolutionVideosAvailable,
      },
      {
        icon: <BlogPagesIcon />,
        title: 'Influencer Hub',
        key: routes.INFLUENCER_HUB,
        isAvailable: isInfluencerHubAvailable,
      },
    ],
  },
  {
    title: 'Study Tool Pages',
    items: [
      {
        icon: <CalendarIcon />,
        title: 'Custom Schedule',
        key: routes.STUDY_SCHEDULE,
        isAvailable: isStudyScheduleAvailable,
      },
      {
        icon: <CanadianDatTipsIcon />,
        title: 'Canadian DAT Tips',
        key: routes.CANADIAN_DAT_TIPS,
        isAvailable: isCanadianDatTipsAvailable,
      },
      {
        icon: <CheatSheetsIcon />,
        title: 'Chapter Notes',
        key: routes.CHAPTER_NOTES,
        isAvailable: isChapterNotesAvailable,
      },
      {
        icon: <CaseBasedScenarioIcon />,
        title: (
          <>
            Case-Based
            <br />
            Scenarios
          </>
        ),
        key: routes.CASE_BASED_SCENARIO_LIST,
        isAvailable: (slug) => isCaseBasedScenariosAvailable(slug) && isCaseScenarioPlatform(slug),
      },
      {
        icon: <CaseBasedScenarioIcon />,
        title: 'Question Banks',
        key: routes.SCENARIOS_QB_LIST,
        isAvailable: (slug) => isScenariosQBanksAvailable(slug) && isCaseScenarioPlatform(slug),
      },
      {
        icon: <BlogPagesIcon />,
        title: 'CARS Question Banks',
        key: routes.CARS_QUESTION_BANK_LIST,
        isAvailable: isCarsQuestionBanksAvailable,
      },
      {
        icon: <MarkedQuestionsIcon />,
        title: 'Marked Questions',
        key: routes.MARKED_QUESTIONS,
        isAvailable: isMarkedQuestionsAvailable,
      },
      // TODO: looks like we don't use these descriptions on web, so we can remove it from cms, need to confirm usage in mobile app
      {
        icon: <VideosIcon />,
        title: 'Video Content',
        key: routes.VIDEO_CONTENT,
        isAvailable: isVideoContentAvailable,
      },
      {
        icon: <FullLengthTestsIcon />,
        title: 'Platform Mock',
        key: routes.PLATFORM_MOCK,
        isAvailable: (slug) => isCommonPtAvailable(PracticeTestTypeEnum.MockTest, slug),
      },
      {
        icon: <FullLengthTestsIcon />,
        title: 'In-Person Mock',
        key: routes.IN_PERSON_MOCK,
        isAvailable: (slug) => isCommonPtAvailable(PracticeTestTypeEnum.InPersonMockTest, slug),
      },
      {
        icon: <FullLengthTestsIcon />,
        title: 'Practice Tests',
        key: routes.COMMON_PTS,
        isAvailable: (slug) => isCommonPtAvailable(PracticeTestTypeEnum.PracticeTest, slug),
      },
      {
        icon: <FullLengthTestsIcon />,
        title: 'Practice Passages',
        key: routes.CARS_PPS,
        isAvailable: (slug) => isCommonPtAvailable(PracticeTestTypeEnum.CarsPracticePassage, slug),
      },
      {
        icon: <CarsPracticeTestsIcon />,
        title: 'Practice Tests',
        key: routes.CARS_PTS,
        isAvailable: (slug) => isCommonPtAvailable(PracticeTestTypeEnum.CarsPracticeTest, slug),
      },
      {
        icon: <DailyWarmupIcon />,
        title: 'Daily CARS',
        key: routes.DAILY_CARS,
        isAvailable: (slug) => isCommonPtAvailable(PracticeTestTypeEnum.DailyCarsPractice, slug),
      },
      {
        icon: <CreateTestIcon />,
        title: 'Create a Test',
        key: routes.CREATE_TEST,
        isAvailable: isCreateTestAvailable,
      },
      {
        icon: <PATGeneratorsIcon />,
        title: 'PAT Generators',
        key: routes.PAT_GENERATORS,
        subjectTabNames: [CourseName.PerceptualAbility],
      },
      {
        icon: <FullLengthTestsIcon />,
        title: 'Full/Mini Tests',
        key: routes.FULL_LENGTH_TESTS,
        isAvailable: isFullMiniTestsAvailable,
      },
      {
        icon: <PastTestsResultsIcon />,
        title: 'Past Test Results',
        key: routes.PAST_TEST_RESULTS,
        isAvailable: isPastTestResultsAvailable,
      },
      {
        icon: <PastTestsResultsIcon />,
        title: 'Practice results',
        key: routes.PRACTICE_RESULTS,
        isAvailable: isPracticeResultsAvailable,
      },
      {
        icon: <PerformanceIcon />,
        title: 'Performance',
        key: routes.PERFORMANCE,
        isAvailable: isPerformanceAvailable,
      },
      {
        icon: <AchievementsIcon />,
        title: 'Achievements',
        key: routes.ACHIEVEMENTS,
        isAvailable: isAchievementsAvailable,
      },
      {
        icon: <DailyWarmupIcon />,
        title: 'Daily Warmup',
        key: routes.DAILY_WARMUP,
        isAvailable: isDailyWarmupAvailable,
      },
      {
        icon: <DailyWarmupIcon />,
        title: 'Daily Goal',
        key: routes.DAILY_GOAL,
        isAvailable: isDailyGoalAvailable,
      },
      {
        icon: <GamepadIcon />,
        title: 'Game Challenges',
        key: routes.GAME_CHALLENGES,
        isAvailable: isGameChallengesAvailable,
      },
      {
        icon: <FacultyIcon />,
        title: 'Content Completion',
        key: routes.CONTENT_COMPLETION,
        isAvailable: isContentCompletionAvailable,
      },
    ],
  },
  {
    title: 'Memberships',
    items: [
      USERS_MENU_ITEM,
      {
        icon: <PerformanceIcon />,
        title: 'Statistics',
        key: routes.STATISTICS,
        isAvailable: isStatisticsAvailable,
      },
      {
        icon: <AccountSharingIcon />,
        title: 'Account Sharing',
        key: routes.ACCOUNT_SHARING,
        isUniversal: true,
      },
      DISCOUNT_CODES_MENU_ITEM,
      {
        icon: <GraduationCapIcon />,
        title: 'Institutional Codes',
        key: routes.INSTITUTIONAL_CODES,
        isAvailable: isInstitutionalCodesAvailable,
      },
      {
        icon: <GridPlusIcon />,
        title: 'Products',
        key: routes.PRODUCTS,
        isAvailable: isProductsAvailable,
      },
      {
        icon: <QuestionsIcon />,
        title: 'Upgrade Page',
        key: routes.UPGRADE_PAGE,
        isAvailable: isUpgradePageAvailable,
        canAccess: ({ isSuperAdmin, platformSlug }) => isSuperAdmin || isRealStudentReviewsAvailable(platformSlug),
      },
      {
        icon: <EmailsIcon />,
        title: 'Emails',
        key: routes.EMAILS,
        isAvailable: isEmailsAvailable,
      },
      {
        icon: <QuestionsIcon />,
        title: 'FAQ',
        key: routes.FAQ,
        isAvailable: isFaqAvailable,
      },
    ],
  },
]

const MARKETING_GROUP_MENU: MenuConfig = [
  {
    title: 'General Pages',
    items: [BLOG_PAGES_MENU_ITEM, COMMON_ELEMENTS_MENU_ITEM],
  },
  {
    title: 'Memberships',
    items: [USERS_MENU_ITEM, DISCOUNT_CODES_MENU_ITEM],
  },
]

const ADMISSION_MENU: MenuConfig = [
  {
    title: 'Management',
    items: [
      {
        icon: <BlogPagesIcon />,
        title: 'Dentistry',
        key: routes.DENTISTRY,
      },
      {
        icon: <VideosIcon />,
        title: 'Optometry',
        key: routes.OPTOMETRY,
      },
    ],
  },
]

const getMenuForPlatform = (groups: string[], platformSlug?: PlatformSlug) => {
  if (groups.includes(AI_TRAINER)) {
    return [
      {
        ...AI_MENU[0],
        items: [AI_MENU_TRAINING_ITEM],
      },
    ]
  }

  if (groups.includes(MARKETING_GROUP)) {
    return MARKETING_GROUP_MENU
  }

  if (platformSlug === PlatformSlug.Admissions) {
    return ADMISSION_MENU
  }

  return MENU
}

type Props = {
  selectedPlatformKey: string
  onCloseMenu: () => void
}

export const Menu = ({ selectedPlatformKey, onCloseMenu }: Props) => {
  const location = useLocation()
  const { groups, isSuperAdmin } = useAuthContext()

  const { selectedPlatform } = usePlatformsAndSubjects({})
  const platformSlug = selectedPlatform?.slug as PlatformSlug | undefined
  const subjects = selectedPlatform?.subjects ?? []
  const accessContext: MenuAccessContext = { subjects, isSuperAdmin, groups, platformSlug }

  const menuForPlatform = useMemo(() => getMenuForPlatform(groups, platformSlug), [groups, platformSlug])

  return (
    <div css={classes.wrapper}>
      {menuForPlatform
        .filter(({ isAvailable }) => !isAvailable || isAvailable(platformSlug))
        .map(({ title, items }) => {
          const visibleItems = items.filter((item) => isItemVisible(item, accessContext))

          if (!visibleItems.length) {
            return null
          }

          return (
            <div key={title}>
              <div css={classes.groupTitle}>{title}</div>
              <div css={classes.items}>
                {visibleItems.map(({ icon, title: itemTitle, key, disabled, isUniversal }) => {
                  if (disabled) {
                    return (
                      <div key={key} css={classes.item({ isDisabled: true })}>
                        {icon}
                        <div>{itemTitle}</div>
                        <ArrowRightIcon className="arrow-icon" />
                      </div>
                    )
                  }

                  return (
                    // eslint-disable-next-line jsx-a11y/anchor-is-valid
                    <Link
                      to={`/${selectedPlatformKey}/${key}`}
                      key={key}
                      css={classes.item({
                        isActive: false,
                        isCurrent: !!matchPath(`/${selectedPlatformKey}/${key}`, location.pathname),
                      })}
                      onClick={onCloseMenu}
                    >
                      {icon}
                      <div css={classes.menuItemTitle}>
                        <span>{itemTitle}</span>
                        {isUniversal && (
                          <div css={classes.universalLabel}>
                            <UniversalIcon />
                          </div>
                        )}
                      </div>
                      <ArrowRightIcon className="arrow-icon" />
                    </Link>
                  )
                })}
              </div>
            </div>
          )
        })}
    </div>
  )
}
